// src/app/hooks/use-breakpoint.ts
import { useState, useEffect } from "react";

export type Breakpoint = "mobile" | "tablet" | "desktop";

/**
 * Breakpoint definitions (match Tailwind defaults):
 *   mobile:  < 768px
 *   tablet:  768px – 1023px
 *   desktop: >= 1024px
 *
 * Contract:
 * - Navigation switches to an off-canvas drawer at "mobile".
 * - The AI Assistant sidebar is hidden below "desktop".
 * - Pages also use "desktop" to decide whether to reserve right-margin
 *   for the AI sidebar (mr-16 / mr-[380px]).
 */
export function useBreakpoint(): Breakpoint {
  const compute = (): Breakpoint => {
    if (typeof window === "undefined") return "desktop"; // SSR default
    const w = window.innerWidth;
    if (w < 768) return "mobile";
    if (w < 1024) return "tablet";
    return "desktop";
  };

  const [bp, setBp] = useState<Breakpoint>(compute);

  useEffect(() => {
    const onResize = () => setBp(compute());
    // Catch any hydration mismatch
    onResize();
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  return bp;
}

/**
 * Convenience booleans. Use this when you want simple flags instead of strings.
 */
export function useResponsive() {
  const bp = useBreakpoint();
  return {
    bp,
    isMobile:  bp === "mobile",
    isTablet:  bp === "tablet",
    isDesktop: bp === "desktop",
    /** true on tablet or desktop — used to decide whether to show the fixed left nav */
    isWide:    bp !== "mobile",
  };
}